import type {Evaluator} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import {hashObject} from "@cm/utils/hashing"
import {ImageNode} from "#template/nodes/image/image-node"
import type {OnFilterActiveContext, TemplateNodeContext, TemplateNodeDefinition, VisitorNodeResult} from "#template/template-node/types"
import {skipped} from "#template/template-node/constants"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {ImageLikeNode, NumberLike} from "#template/nodes/types"
import {imageLike} from "#template/nodes/image/fundamental"
import {numberLike} from "#template/nodes/number/fundamental"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {ImageGenerator, InputMaterialConnection, MaterialConnection, OutputMaterialConnection, ScalarMathNodeOperation} from "@cm/material-nodes"
import {ScalarMath, scalarMathNodeOperation, color, combineRgb, mathOp, value, materialConnection, SeparateRgb, CombineRgb} from "@cm/material-nodes"
import {visitNone} from "#template/template-node/visit-helpers"

export const imageOperatorOperation = z.union([z.literal("SMOOTH_STEP"), z.literal("BLEND_NORMALS"), scalarMathNodeOperation])
export type ImageOperatorOperation = z.infer<typeof imageOperatorOperation>

function setupMathOperation(
    operation: ScalarMathNodeOperation,
    value: InputMaterialConnection<"float"> | number,
    value_001: InputMaterialConnection<"float"> | number,
): OutputMaterialConnection<"float"> {
    return materialConnection(
        new ScalarMath({
            parameters: {
                inputs: {
                    ...(typeof value === "number" ? {} : {value}),
                    ...(typeof value_001 === "number" ? {} : {value_001}),
                },
                settings: {
                    operation,
                    ...(typeof value === "number" ? {value} : {}),
                    ...(typeof value_001 === "number" ? {value_001} : {}),
                },
            },
        }),
        "value",
    )
}

function prepareVectorizedInput(input: InputMaterialConnection<"color"> | number): {
    r: InputMaterialConnection<"float"> | number
    g: InputMaterialConnection<"float"> | number
    b: InputMaterialConnection<"float"> | number
} {
    if (typeof input === "number") return {r: input, g: input, b: input}
    const separateRgb = new SeparateRgb({
        parameters: {
            inputs: {
                image: input,
            },
            settings: {},
        },
    })
    return {r: materialConnection(separateRgb, "r"), g: materialConnection(separateRgb, "g"), b: materialConnection(separateRgb, "b")}
}

function prepareVectorizedOutput(output: {
    r: InputMaterialConnection<"float">
    g: InputMaterialConnection<"float">
    b: InputMaterialConnection<"float">
}): OutputMaterialConnection<"color"> {
    return materialConnection(
        new CombineRgb({
            parameters: {
                inputs: {
                    r: output.r,
                    g: output.g,
                    b: output.b,
                },
                settings: {},
            },
        }),
        "image",
    )
}

function blendNormals(
    normal1: InputMaterialConnection<"float"> | number,
    normal2: InputMaterialConnection<"float"> | number,
): OutputMaterialConnection<"color"> {
    const vectorizedNormal1 = prepareVectorizedInput(normal1)
    const vectorizedNormal2 = prepareVectorizedInput(normal2)

    const mul = (a: InputMaterialConnection<"float"> | number, b: InputMaterialConnection<"float"> | number) => setupMathOperation("MULTIPLY", a, b)
    const add = (a: InputMaterialConnection<"float"> | number, b: InputMaterialConnection<"float"> | number) => setupMathOperation("ADD", a, b)
    const sub = (a: InputMaterialConnection<"float"> | number, b: InputMaterialConnection<"float"> | number) => setupMathOperation("SUBTRACT", a, b)
    const mulAdd = (a: InputMaterialConnection<"float"> | number, b: InputMaterialConnection<"float"> | number, c: MaterialConnection<number> | number) =>
        add(mul(a, b), c)

    const tx = mulAdd(vectorizedNormal1.r, 2, -1)
    const ty = mulAdd(vectorizedNormal1.g, 2, -1)
    const tz = mulAdd(vectorizedNormal1.b, 2, 0)

    const ux = mulAdd(vectorizedNormal2.r, -2, 1)
    const uy = mulAdd(vectorizedNormal2.g, -2, 1)
    const uz = mulAdd(vectorizedNormal2.b, 2, -1)

    const dot = add(add(mul(tx, ux), mul(ty, uy)), mul(tz, uz))

    const blendComponent = (tComponent: InputMaterialConnection<"color"> | number, uComponent: InputMaterialConnection<"color"> | number) =>
        mul(add(sub(mul(tComponent, dot), mul(uComponent, tz)), 1), 0.5)

    return prepareVectorizedOutput({
        r: blendComponent(tx, ux),
        g: blendComponent(ty, uy),
        b: blendComponent(tz, uz),
    })
}

function setupNodesForOperation(
    operation: ImageOperatorOperation,
    value: number | InputMaterialConnection<"float">,
    value_001: number | InputMaterialConnection<"float">,
): OutputMaterialConnection<"color"> | OutputMaterialConnection<"float"> {
    const shouldVectorize = !(typeof value === "number" && typeof value_001 === "number")

    const setupVectorizedMathOperation = (
        operation: ScalarMathNodeOperation,
        value: number | InputMaterialConnection<"float">,
        value_001: number | InputMaterialConnection<"float">,
    ): OutputMaterialConnection<"color"> => {
        const vectorizedValue = prepareVectorizedInput(value)
        const vectorizedValue_001 = prepareVectorizedInput(value_001)

        return combineRgb(
            mathOp(operation, vectorizedValue.r, vectorizedValue_001.r),
            mathOp(operation, vectorizedValue.g, vectorizedValue_001.g),
            mathOp(operation, vectorizedValue.b, vectorizedValue_001.b),
        )
    }

    if (operation === "SMOOTH_STEP") {
        const clampedValue = setupNodesForOperation("MINIMUM", setupNodesForOperation("MAXIMUM", value, 0), 1)

        const value_001GreaterThanZero = setupNodesForOperation("GREATER_THAN", value_001, 0)
        const value_001LessOrEqualToZero = setupNodesForOperation("SUBTRACT", 1, value_001)

        // value_001 > 0 branch
        const value_001GreaterThanZeroBranch = (() => {
            const oneMinusClampedValue = setupNodesForOperation("SUBTRACT", 1, clampedValue)
            const pow = setupNodesForOperation("POWER", oneMinusClampedValue, value_001)
            const oneMinusPow = setupNodesForOperation("SUBTRACT", 1, pow)
            const root = setupNodesForOperation("POWER", oneMinusPow, setupNodesForOperation("DIVIDE", 1, value_001))
            return setupNodesForOperation("MULTIPLY", root, value_001GreaterThanZero)
        })()
        // value_001 <= 0 branch
        const value_001LessThanOrEqualToZeroBranch = setupNodesForOperation("MULTIPLY", clampedValue, value_001LessOrEqualToZero)
        return setupNodesForOperation("ADD", value_001GreaterThanZeroBranch, value_001LessThanOrEqualToZeroBranch)
    } else if (operation === "BLEND_NORMALS") {
        return blendNormals(value, value_001)
    } else return shouldVectorize ? setupVectorizedMathOperation(operation, value, value_001) : mathOp(operation, value, value_001)
}

export type ImageOperatorParameters = {
    name: string
    input: ImageLikeNode | NumberLike | null
    operation: ImageOperatorOperation
    value: ImageLikeNode | NumberLike | null
}

const imageOperatorDefinition: TemplateNodeDefinition<ImageOperatorParameters> = {
    nodeClass: "ImageOperator",
    validation: {
        parameters: z.object({
            name: z.string(),
            input: z.nullable(z.union([imageLike, numberLike])),
            operation: imageOperatorOperation,
            value: z.nullable(z.union([imageLike, numberLike])),
        }),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class ImageOperator extends ImageNode<ImageOperatorParameters> {
    public override get definition() {
        return imageOperatorDefinition
    }

    public override onFilterActive(_context: OnFilterActiveContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<ImageOperatorParameters> {
        const {input, value} = this.parameters
        if (input === null || value === null) return skipped
        return visitNone(this.parameters)
    }

    public override evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderInlet<ImageGenerator | null> {
        const {input, ...rest} = this.parameters

        const evaluatedInputParam = input === null ? null : evaluator.evaluate(input)
        const evaluatedValueParam = rest.value === null ? null : evaluator.evaluate(rest.value)

        return scope.pureLambda<[ImageGenerator | number | null, ImageOperatorParameters["operation"], ImageGenerator | number | null], ImageGenerator | null>(
            scope.tuple(evaluatedInputParam, rest.operation, evaluatedValueParam),
            ([inputParam, operation, valueParam]) => {
                if (inputParam === null || valueParam === null) return null

                const parseParam = (
                    imageGeneratorOrNumber: number | ImageGenerator,
                ): {generator: ImageGenerator["imageNode"]["generator"]; hash: string | number; metadata: ImageGenerator["metadata"]} => {
                    if (typeof imageGeneratorOrNumber === "number") {
                        return {
                            generator: () => ({
                                color: color({
                                    r: imageGeneratorOrNumber,
                                    g: imageGeneratorOrNumber,
                                    b: imageGeneratorOrNumber,
                                }),
                                alpha: value(1),
                            }),
                            hash: imageGeneratorOrNumber,
                            metadata: undefined,
                        }
                    } else {
                        return {
                            generator: imageGeneratorOrNumber.imageNode.generator,
                            hash: imageGeneratorOrNumber.imageNode.hash,
                            metadata: imageGeneratorOrNumber.metadata,
                        }
                    }
                }

                const parsedInputParam = parseParam(inputParam)
                const parsedValueParam = parseParam(valueParam)

                const metadata = (() => {
                    const inputWidth = parsedInputParam.metadata?.width
                    const inputHeight = parsedInputParam.metadata?.height
                    const valueWidth = parsedValueParam.metadata?.width
                    const valueHeight = parsedValueParam.metadata?.height

                    const width = inputWidth !== undefined && valueWidth !== undefined ? Math.max(inputWidth, valueWidth) : (inputWidth ?? valueWidth)
                    const height = inputHeight !== undefined && valueHeight !== undefined ? Math.max(inputHeight, valueHeight) : (inputHeight ?? valueHeight)

                    return {
                        width,
                        height,
                    }
                })()

                return {
                    imageNode: {
                        generator: (generatorData) => {
                            const inputParamGeneratorOutput = parsedInputParam.generator(generatorData)
                            const valueParamGeneratorOutput = parsedValueParam.generator(generatorData)

                            const color = setupNodesForOperation(operation, inputParamGeneratorOutput.color, valueParamGeneratorOutput.color)
                            const alpha = inputParamGeneratorOutput.alpha // Use input parameter as source for output alpha channel

                            return {
                                color,
                                alpha,
                            }
                        },
                        hash: hashObject({
                            type: "ImageOperator",
                            inputImage: parsedInputParam.hash,
                            operation,
                            value: parsedValueParam.hash,
                        }),
                    },
                    metadata,
                }
            },
            "imageOperator",
        )
    }
}
